import type { SortKey } from 'hooks/useDataControl';
import type { Column } from './columns';
import { useState, useEffect, Fragment } from 'react';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table';
import { useIsMobile } from '@/components/ui/hooks/use-mobile';
import { useDataControl, type UseDataControlReturn } from 'hooks/useDataControl';
import { cn } from 'helpers/cn';
import { isColumnVisible, renderColumnValue } from './columns';
import { DataTableCards } from './DataTableCards';
import { SortHeadCell } from './SortControls';
import { PaginationBar } from './PaginationBar';

export const DataTable = <T,>(props: DataTableProps<T>) => {
  const [columns, setColumns] = useState<Column<T>[]>([]);
  const { dataController, uniqueKey, noDataCTA, noDataMessage, noMatchMessage, renderExpanded } = props;
  const theUniqueKey = uniqueKey || 'id';
  const { rawData, data, pageSize, setCustomSort, searchQuery } = dataController;
  const isMobile = useIsMobile();

  useEffect(() => {
    if (props.columns) {
      setColumns(props.columns);
    } else if (!!rawData && rawData.length > 0) {
      const cols: Column<T>[] = Object.keys(rawData[0] as object).map((key) => ({
        field: key as keyof T,
        label: toLabel(key),
        sortKey: key as SortKey<T>,
      }));
      setColumns(cols);
    } else {
      setColumns([]);
    }
  }, [props.columns, rawData]);

  useEffect(() => {
    if (typeof props.pageSize === 'number') {
      dataController.setPageSize(props.pageSize);
    }
  }, [props.pageSize]);

  useEffect(() => {
    if (!!props.customSort) {
      setCustomSort(() => props.customSort!);
    }
  }, [props.customSort]);

  useEffect(() => {
    if (props.searchKeys) {
      const propsSearchKeys = JSON.stringify(props.searchKeys);
      const controllerSearchKeys = JSON.stringify(dataController.searchKeys);
      if (propsSearchKeys !== controllerSearchKeys) {
        dataController.setSearchKeys(props.searchKeys);
      }
    } else if (columns.length > 0 && !dataController.searchKeys) {
      const colFields = columns.map((col) => col.field).filter((f): f is keyof T => !!f);
      dataController.setSearchKeys(colFields);
    }
  }, [props.searchKeys, columns.length]);

  if (!rawData) return null;

  const pageData = data ?? [];
  const query = searchQuery.trim();

  /*
   * Two different nothings.
   *
   * This branch used to test `rawData.length`, which is the *unfiltered* input, so a
   * search that excluded every row fell through to the table below: a header row with
   * no body on desktop, and — since cards have no headers — a blank panel on a phone.
   * Nothing said the search was the reason, so it read as a broken screen.
   *
   * `noDataMessage` still belongs to the empty set, because screens use it to explain
   * the feature to a first-time reader ("no surveys yet, here is what a survey is").
   * Telling someone that after they typed a query they can see in the field would say
   * their data is gone.
   */
  if (pageData.length === 0) {
    const isFilteredOut = query.length > 0 && rawData.length > 0;

    return (
      <div className="px-4 py-10">
        <div className="supporting">
          {isFilteredOut ? (noMatchMessage ?? `No results for "${query}".`) : noDataMessage || 'No Data Found.'}
        </div>
        {/* The CTA creates the first item, which is not what a failed search needs. */}
        {!isFilteredOut && noDataCTA && <>{noDataCTA}</>}
      </div>
    );
  }

  const showPagination = !!pageSize && pageSize < rawData.length;

  return (
    <div className="bg-panel border-line rounded-card overflow-hidden border">
      {isMobile ? (
        <DataTableCards
          columns={columns}
          controller={dataController}
          uniqueKey={theUniqueKey as keyof T}
          renderExpanded={renderExpanded}
        />
      ) : (
        <div className="overflow-x-auto">
          {/* 13px, not the shadcn 14px default: every primary title in the console —
              panel heads, cards, dialogs — is 13px, and rows a size larger read as a
              different font sitting beside the 10px mono labels. */}
          <Table className="text-body">
            <TableHeader className="bg-panel2">
              <TableRow>
                {columns.map(
                  (column: Column<T>, idx: number) =>
                    isColumnVisible(column) && <SortHeadCell key={idx} column={column} controller={dataController} />,
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageData.map((item: T, idx: number) => {
                const expanded = renderExpanded?.(item) ?? null;
                return (
                  <Fragment key={String(item[theUniqueKey as keyof T] || idx)}>
                    <TableRow className={cn('group/tablerow', expanded !== null && 'border-b-0')}>
                      {columns.map(
                        (column, columnIdx) =>
                          isColumnVisible(column) && (
                            <TableCell
                              className={cn('whitespace-nowrap max-[499px]:px-2', column.cellClassName)}
                              key={columnIdx}
                            >
                              <>{renderColumnValue(column, item, pageData, idx)}</>
                            </TableCell>
                          ),
                      )}
                    </TableRow>
                    {/* Hover off: the detail is part of the row above, not a row of its own. */}
                    {expanded !== null && (
                      <TableRow className="hover:bg-transparent">
                        <TableCell colSpan={columns.filter(isColumnVisible).length} className="whitespace-normal pt-0">
                          {expanded}
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {showPagination && <PaginationBar dataManager={dataController as UseDataControlReturn<T>} />}
    </div>
  );
};

export { useDataControl };
export type { UseDataControlReturn };
export type { Column, DataTableFormatArgs, MobileColumnRole } from './columns';

type DataTableProps<T> = {
  dataController: UseDataControlReturn<T>;
  columns?: Column<T>[];
  uniqueKey?: keyof T;
  searchKeys?: (keyof T)[];
  pageSize?: number;
  noDataCTA?: React.ReactNode;
  customSort?: (a: T, b: T, originalSort: (a: T, b: T) => number) => number;
  /** Shown when the data set is genuinely empty. */
  noDataMessage?: string;
  /** Shown when a search excluded everything. Defaults to naming the query. */
  noMatchMessage?: string;
  /**
   * Detail shown under a row, or null to show none. The screen owns which rows are
   * open, so this is a render function rather than a toggle the table keeps: the open
   * state then survives paging and re-sorting, and the table stays stateless about it.
   */
  renderExpanded?: (item: T) => React.ReactNode | null;
};

function toLabel(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // insert space before capitals
    .replace(/_/g, ' ') // replace underscores with spaces
    .replace(/\s+/g, ' ') // collapse multiple spaces
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}
